/**
 * Legal Document DOCX Generator
 *
 * Generates professionally formatted legal documents as DOCX files using the docx library.
 * Follows the same block-parsing pattern as formattingDocx.ts.
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Footer,
  PageNumber,
  AlignmentType,
  convertInchesToTwip,
} from 'docx';

// ============================================================================
// Types
// ============================================================================

export interface BuildLegalDocDocxParams {
  /** The full generated document text (markdown) */
  content: string;
  /** Document title */
  title: string;
  /** Creation date */
  createdAt?: Date;
}

// ============================================================================
// Block Parser (matches formattingDocx.ts structure)
// ============================================================================

type BlockKind = 'title' | 'section' | 'subsection' | 'bullet' | 'numbered' | 'paragraph';

interface Block {
  kind: BlockKind;
  text: string;
}

function stripMarkdownFormatting(input: string): string {
  let out = input || '';
  out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  out = out.replace(/\*\*\*(.+?)\*\*\*/g, '$1');
  out = out.replace(/\*\*(.+?)\*\*/g, '$1');
  out = out.replace(/__(.+?)__/g, '$1');
  out = out.replace(/\*([^*\n]+)\*/g, '$1');
  out = out.replace(/_([^_\n]+)_/g, '$1');
  out = out.replace(/`+([^`]+)`+/g, '$1');
  return out.trim();
}

function parseBlocks(content: string): Block[] {
  const blocks: Block[] = [];
  const lines = (content || '').split('\n');

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    // Title (single #)
    if (line.startsWith('# ') && !line.startsWith('## ')) {
      blocks.push({ kind: 'title', text: stripMarkdownFormatting(line.slice(2)) });
      continue;
    }

    // Section header (##)
    if (line.startsWith('## ')) {
      blocks.push({ kind: 'section', text: stripMarkdownFormatting(line.slice(3)) });
      continue;
    }

    // Subsection header (###)
    if (line.startsWith('### ')) {
      blocks.push({ kind: 'subsection', text: stripMarkdownFormatting(line.slice(4)) });
      continue;
    }

    // Bullet points
    if (/^[-*]\s+/.test(line)) {
      blocks.push({ kind: 'bullet', text: stripMarkdownFormatting(line.replace(/^[-*]\s+/, '')) });
      continue;
    }

    // Numbered lists
    const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numMatch) {
      blocks.push({ kind: 'numbered', text: `${numMatch[1]}. ${stripMarkdownFormatting(numMatch[2])}` });
      continue;
    }

    // Default: paragraph
    blocks.push({ kind: 'paragraph', text: stripMarkdownFormatting(line) });
  }

  return blocks;
}

// ============================================================================
// DOCX Builder
// ============================================================================

// copy: legal_doc_disclaimer
const DISCLAIMER_TEXT =
  'This document was generated for informational purposes only. Consult a licensed attorney before use.';

export async function buildLegalDocDocx(params: BuildLegalDocDocxParams): Promise<File> {
  const createdAt = params.createdAt || new Date();
  const dateStr = createdAt.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const displayTitle = params.title || 'Legal Document';
  const blocks = parseBlocks(params.content);

  // -------------------------------------------------------------------------
  // Title page paragraphs
  // -------------------------------------------------------------------------
  const titlePageChildren: Paragraph[] = [
    new Paragraph({ spacing: { before: 2000 } }),

    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: displayTitle,
          bold: true,
          size: 36, // 18pt
          font: 'Arial',
        }),
      ],
    }),

    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 800 },
      children: [
        new TextRun({
          text: dateStr,
          size: 22, // 11pt
          font: 'Arial',
          color: '555555',
        }),
      ],
    }),

    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: DISCLAIMER_TEXT,
          italics: true,
          size: 18, // 9pt
          font: 'Arial',
          color: '808080',
        }),
      ],
    }),
  ];

  // -------------------------------------------------------------------------
  // Content paragraphs
  // -------------------------------------------------------------------------
  const contentChildren: Paragraph[] = [];

  for (const block of blocks) {
    switch (block.kind) {
      case 'title':
        contentChildren.push(
          new Paragraph({
            heading: HeadingLevel.TITLE,
            alignment: AlignmentType.CENTER,
            spacing: { before: 400, after: 160 },
            children: [
              new TextRun({
                text: block.text,
                bold: true,
                size: 36, // 18pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'section':
        contentChildren.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 360, after: 120 },
            children: [
              new TextRun({
                text: block.text,
                bold: true,
                size: 26, // 13pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'subsection':
        contentChildren.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 80 },
            children: [
              new TextRun({
                text: block.text,
                bold: true,
                size: 22, // 11pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'bullet':
        contentChildren.push(
          new Paragraph({
            bullet: { level: 0 },
            spacing: { before: 60, after: 60 },
            indent: { left: convertInchesToTwip(0.33) },
            children: [
              new TextRun({
                text: block.text,
                size: 22, // 11pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'numbered':
        contentChildren.push(
          new Paragraph({
            spacing: { before: 60, after: 60 },
            indent: { left: convertInchesToTwip(0.33) },
            children: [
              new TextRun({
                text: block.text,
                size: 22, // 11pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;

      case 'paragraph':
      default:
        contentChildren.push(
          new Paragraph({
            spacing: { before: 120, after: 200 },
            children: [
              new TextRun({
                text: block.text,
                size: 22, // 11pt
                font: 'Arial',
              }),
            ],
          })
        );
        break;
    }
  }

  // -------------------------------------------------------------------------
  // Footer with page numbers and disclaimer
  // -------------------------------------------------------------------------
  const contentFooter = new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            children: ['Page ', PageNumber.CURRENT, ' of ', PageNumber.TOTAL_PAGES],
            size: 16, // 8pt
            font: 'Arial',
            color: '666666',
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: DISCLAIMER_TEXT,
            italics: true,
            size: 14, // 7pt
            font: 'Arial',
            color: '999999',
          }),
        ],
      }),
    ],
  });

  // -------------------------------------------------------------------------
  // Assemble document
  // -------------------------------------------------------------------------
  const doc = new Document({
    title: displayTitle,
    creator: 'Legal Document Creator',
    description: 'Legal Document',
    sections: [
      // Title page section (no footer)
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
          },
        },
        children: titlePageChildren,
      },
      // Content section with footer
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
            },
          },
        },
        footers: {
          default: contentFooter,
        },
        children: contentChildren,
      },
    ],
  });

  // -------------------------------------------------------------------------
  // Serialize and return as File
  // -------------------------------------------------------------------------
  const blob = await Packer.toBlob(doc);

  const datePart = createdAt.toISOString().slice(0, 10);
  const filenamePart = displayTitle
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  const filename = `${filenamePart}-${datePart}.docx`;

  return new File([blob], filename, {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}
